import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Org-configurable "Page Sections" — admin-created sidebar sections that
 * org-configurable pages (task pages, agent pages, and future page kinds)
 * attach to via their `pageSectionId`, alongside the built-in route-derived
 * sections a page may still target through `navGroup`.
 *
 * A section can also hold built-in module pages (e.g. Supplier Product
 * Matches), named by their route path in `builtInPages`. Listing a page
 * here is additive: it also stays in its code-defined sidebar section (and in
 * any other org section that lists it).
 *
 * Sections are pure grouping: they carry no audience of their own. A section
 * renders in the sidebar only when it holds at least one page the current user
 * can see, so page-level RBAC, audiences and route access stay the single
 * visibility rule.
 */

/**
 * A built-in page's route path (`/accounts-payable/...`). The sidebar matches
 * it against the route tree; the server cannot see routes, so it only checks
 * the shape — a path naming no route (or one the viewer cannot open) is
 * simply not rendered.
 */
export const BuiltInPagePathSchema = z
  .string()
  .max(512)
  .regex(/^\/[^\s$]*$/, "Must be a route path starting with /");

/**
 * One built-in page listed in a section. `label` overrides the page's sidebar
 * name in this section only; null (or omitted) keeps the page's own name. Rendered
 * verbatim (org data, not translated).
 */
export const BuiltInPageEntrySchema = z.object({
  path: BuiltInPagePathSchema,
  label: z.string().trim().min(1).max(255).nullable().default(null),
});

/**
 * The built-in pages a section holds, in sidebar order. Other sections are
 * unaffected — the same page may be listed in several.
 */
export const BuiltInPagesSchema = z
  .array(BuiltInPageEntrySchema)
  .max(200)
  .refine(
    (pages) => new Set(pages.map((page) => page.path)).size === pages.length,
    { message: "Each built-in page can be listed once" },
  );

// --- Response schema ---

export const PageSectionResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  sortOrder: z.number().int(),
  builtInPages: z.array(
    z.object({ path: z.string(), label: z.string().nullable() }),
  ),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// --- Input schemas ---

/**
 * `pageSectionId` as every page kind's create/update input accepts it: an
 * org-created section (must belong to the caller's org). A page sits in
 * exactly one place — setting this clears the page's `navGroup` and vice
 * versa; sending both non-null is rejected.
 */
export const PageSectionIdWriteSchema = z.uuid().nullable().optional();

/**
 * Sidebar sort order accepted on writes for pages and sections alike. The
 * editors validate their raw input against this same schema so a value the
 * API would reject is flagged before save.
 */
export const SidebarSortOrderSchema = z.number().int().min(0).max(10000);

const PageSectionWriteShape = {
  name: z.string().trim().min(1).max(255),
  sortOrder: SidebarSortOrderSchema.optional(),
  builtInPages: BuiltInPagesSchema.optional(),
} as const;

export const CreatePageSectionInputSchema = z.object(PageSectionWriteShape);

export const UpdatePageSectionInputSchema = z.object({
  id: z.uuid(),
  ...PageSectionWriteShape,
  name: PageSectionWriteShape.name.optional(),
});

const PageSectionIdInputSchema = z.object({ id: z.uuid() });

/**
 * The slice of a section the sidebar needs to place built-in pages: only
 * sections holding at least one built-in page are returned.
 */
export const BuiltInPagePlacementSectionSchema = PageSectionResponseSchema.pick(
  { id: true, name: true, sortOrder: true, builtInPages: true },
);

const ArchivePageSectionOutputSchema = z.object({ success: z.boolean() });

// --- Contracts ---

export const pageSectionsContracts = {
  // Every section in the org. Readable by anyone who can see any kind of
  // org-configurable page (the sidebar needs the titles); administered by
  // anyone who can administer any kind of page.
  list: oc.output(z.array(PageSectionResponseSchema)).route({
    method: "GET",
    summary: "List sidebar page sections",
    description:
      "Returns every admin-created sidebar section in your organization, with name and sort order, that custom task and agent pages can be grouped under.",
  }),

  // The sections that hold built-in pages. Readable by every org member:
  // built-in pages are visible to users with no page grants at all, and the
  // sidebar must know where to draw them. Names and paths only.
  builtInPlacements: oc
    .output(z.array(BuiltInPagePlacementSectionSchema))
    .route({
      method: "GET",
      summary: "List built-in page placements",
      description:
        "Returns the admin-created sidebar sections that hold built-in module pages, with each section's name, sort order and the built-in pages listed in it (route path and optional sidebar label override).",
    }),

  create: oc
    .input(CreatePageSectionInputSchema)
    .output(PageSectionResponseSchema)
    .route({
      method: "POST",
      summary: "Create a sidebar page section",
      description:
        "Creates a named sidebar section with an optional sort order for grouping your organization's custom pages, optionally also listing built-in module pages (by route path, with an optional sidebar label override) in it.",
    }),

  update: oc
    .input(UpdatePageSectionInputSchema)
    .output(PageSectionResponseSchema)
    .route({
      method: "PUT",
      summary: "Update a sidebar page section",
      description:
        "Renames a sidebar page section, changes its sort order, or replaces the built-in module pages (by route path, with an optional sidebar label override) listed in it.",
    }),

  // Archiving a section detaches its pages (they fall back to their navGroup
  // or the generic "Pages" section); the pages themselves are untouched.
  archive: oc
    .input(PageSectionIdInputSchema)
    .output(ArchivePageSectionOutputSchema)
    .route({
      method: "DELETE",
      summary: "Archive a sidebar page section",
      description:
        "Archives a sidebar section; its pages are detached and fall back to their default sidebar group. The pages themselves are not changed.",
    }),
} as const;

// --- Type exports ---

export type PageSectionResponse = z.infer<typeof PageSectionResponseSchema>;
export type CreatePageSectionInput = z.infer<
  typeof CreatePageSectionInputSchema
>;
export type UpdatePageSectionInput = z.infer<
  typeof UpdatePageSectionInputSchema
>;
export type BuiltInPageEntry = z.infer<typeof BuiltInPageEntrySchema>;
export type BuiltInPagePlacementSection = z.infer<
  typeof BuiltInPagePlacementSectionSchema
>;
